import _ from 'underscore';
import {NetxThemePref, userPref} from '@netx/core-app/properties/theme';

import config from '@netx/core-app/config';
import Netx, {ThemeMode} from '@netx/core/bootstrap';
import {isPortal} from '@netx/core/lib/utils';
import {getPreference} from '@netx/core-user/utils/getPreference';

const customPropertyPrefix = config.CSS_CUSTOM_PROPERTY_PREFIX;

export type CustomCssProperties = {
	/** #hex-color */
	'dark-on-primary': string;
	'dark-on-primary-custom': 'true' | 'false';
	/** #hex-color */
	'dark-on-secondary': string;
	'dark-on-secondary-custom': 'true' | 'false';
	/** #hex-color */
	'dark-primary': string;
	/** #hex-color */
	'dark-secondary': string;
	/** #hex-color */
	'light-on-primary': string;
	'light-on-primary-custom': 'true' | 'false';
	/** #hex-color */
	'light-on-secondary': string;
	'light-on-secondary-custom': 'true' | 'false';
	/** #hex-color */
	'light-primary': string;
	/** #hex-color */
	'light-secondary': string;
	/** id as string */
	'login-background-assetId': string;
	/** timestamp as string */
	updated: string;
};

const THEME_PREFS = Object.values(NetxThemePref);

// Will store all css properties here
let currentProperties: CustomCssProperties = {
	updated: '0',
	'login-background-assetId': '',
	'light-on-primary-custom': 'false',
	'light-on-secondary-custom': 'false',
	'dark-on-primary-custom': 'false',
	'dark-on-secondary-custom': 'false',
	...config.CSS_CUSTOM_PROPERTIES,
};

let currentThemeMode: ThemeMode = NetxThemePref.LIGHT;
let fetchedCSS = false;

const documentElement = document.documentElement;
const style = documentElement.style;

/**
 * @function
 * @param key Style property key
 * @param value Style property value
 */
const setStyleProperty = (key: string, value: string) => {
	if (value) {
		return style.setProperty(key, value);
	}

	// Look for default value
	const defaultValue =
		config.CSS_CUSTOM_PROPERTIES[
			`${currentThemeMode}-${key.replace(customPropertyPrefix, '')}`
		];

	defaultValue
		? style.setProperty(key, defaultValue)
		: style.removeProperty(key);
};

/**
 * Get the current theme mode
 * @function
 * @return Theme mode
 */
Netx.getThemeMode = () => {
	const sessionKey = Netx.session?.getKey();
	const appAuthenticated = sessionKey && sessionKey !== '';
	const tokenAccess = Netx.session?.getToken() != null;

	// No `dark` support for portal currently
	if (isPortal() || tokenAccess || !appAuthenticated)
		return NetxThemePref.LIGHT;

	// First try user prefs then default
	const pref = getPreference(userPref);

	return !pref ||
		pref === NetxThemePref.DEFAULT ||
		!THEME_PREFS.includes(pref as any)
		? window.matchMedia('(prefers-color-scheme: dark)').matches
			? NetxThemePref.DARK
			: NetxThemePref.LIGHT
		: (pref as ThemeMode);
};

/**
 * Get style property from DOM
 * @function
 * @return Style property
 */
Netx.getStyleProperty = (key: string) => {
	const style = getComputedStyle(document.body);
	return style.getPropertyValue(`${customPropertyPrefix}${key}`).trim();
};

/**
 * @function
 * @returns string | undefined
 */
Netx.getLoginBackgroundUrl = () => {
	return !isPortal()
		? `${Netx.URL.loginBackground}?cb=${
				currentProperties.updated || Date.now()
		  }`
		: undefined;
};

/**
 * Changes address bar color in supported browsers
 * @function
 */
Netx.setMetaThemeColor = (value: string) => {
	const meta = document.querySelector('meta[name=theme-color]');
	meta && meta.setAttribute('content', value);
};

/**
 * Set style property on DOM
 * @function
 * @param key Style property key
 * @param value Style property value
 */
Netx.setStyleProperty = (key: string, value: string) => {
	currentProperties[key] = value;

	// We use this for cachebusting the background image since last update
	if (key === 'updated') return false;

	key = key.replace(`${currentThemeMode}-`, '');

	if (key === 'primary') {
		Netx.setMetaThemeColor(value);
	}
	setStyleProperty(`${customPropertyPrefix}${key}`, value);
	return true;
};

/**
 * @function
 * @param name Theme mode name
 * @fires module:netx#theme:toggled
 */
Netx.setThemeMode = (name: ThemeMode, force = false) => {
	// because there are some js files sitting around i am sure
	if (
		!THEME_PREFS.includes(name as any) ||
		(name as any) === NetxThemePref.DEFAULT
	) {
		name = window.matchMedia('(prefers-color-scheme: dark)').matches
			? NetxThemePref.DARK
			: NetxThemePref.LIGHT;
	}

	const themeChanged = currentThemeMode !== name;
	currentThemeMode = name;

	const keys = Object.keys(currentProperties).filter((key) =>
		key.includes(`${currentThemeMode}-`),
	);
	keys.forEach((key) => {
		if (Netx.getStyleProperty(key) === currentProperties[key]) return;
		Netx.setStyleProperty(key, currentProperties[key]);
	});

	if (themeChanged) {
		// Clear theme-mode classes
		THEME_PREFS.forEach((mode) =>
			documentElement.classList.remove(`theme-${mode}`),
		);
		// Add selected theme-mode class
		document.documentElement.classList.add(`theme-${currentThemeMode}`);
	}

	// Dispatch event for the rest of the app to listen to
	(themeChanged || force) && dispatcher.trigger('theme:toggled', name);
};

/**
 * When user prefs are available we can set the theme mode to the user's selection
 * @event module:app#app:user:prefs:ready
 */
// TODO: these `once` dispatchers on the global scope of a file are hard to test from multiple ways
// having a hard time testing the the initialize thoroughly and this having not `fetchedCSS`
dispatcher.once('app:user:prefs:ready', () => {
	window
		.matchMedia('(prefers-color-scheme: dark)')
		.addEventListener('change', (event) => {
			if (!fetchedCSS) return;
			Netx.setThemeMode(Netx.getThemeMode());
		});
	// we will call set theme mode when the css is done fetching
	if (!fetchedCSS) return;
	Netx.setThemeMode(Netx.getThemeMode());
});

const initialize = () => {
	// Set the default theme mode class
	documentElement.classList.add(`theme-${currentThemeMode}`);

	// Load css properties from server
	return new Promise<void>((resolve, reject) => {
		$.ajax({
			cache: false,
			dataType: 'json',
			type: 'GET',
			url: Netx.URL.customCssProperties,
		}).then(
			(data: CustomCssProperties) => {
				Object.assign(
					data,
					_.pick.apply(_, [
						config.CSS_CUSTOM_PROPERTIES,
						..._.difference(
							Object.keys(config.CSS_CUSTOM_PROPERTIES),
							Object.keys(data),
						),
					]),
				);

				// Store all data
				currentProperties = data;
				// Set current theme (will loop over data and set based on theme mode)
				Netx.setThemeMode(Netx.getThemeMode(), true);

				fetchedCSS = true;

				resolve();
			},
			(jqXHR, status, errorThrown) => {
				// What to do?
				reject(errorThrown);
			},
		);
	});
};

export default () => initialize();
